import { Avatar, Box, Button, Divider, Flex, HStack, Tag, Text, VStack, Wrap, WrapItem } from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MdAssignment, MdDescription, MdLocalOffer, MdSchedule, MdVerified } from "react-icons/md";
import bg from "../../../assets/icons/exams.svg";
import PdfPreviewModal, { examPaperPdfUrl } from "../../../components/student/PdfPreviewModal";
import { examRedoRequest } from "../../../modules/students-modules/exams/Actions";
import { studentPalette } from "../../../constants/studentPalette";
import {
  studentOutlineButtonDarkSx,
  studentSurfaces,
} from "../../../constants/studentThemeSurfaces";
import { useStartsInLabel, useStudentExamDeadline } from "../../../hooks/useStartsInLabel";
import { formatApiDateTimeLocal, formatStudentExamDeadlineLine } from "../../../utils/examSchedule";

const POST_EXAM_REDIRECT_KEY = "edutopia_post_exam_redirect";

const C = {
  primary: studentPalette.primary,
  accent: studentPalette.secondary,
  border: studentSurfaces.border,
  cream: studentPalette.cream,
  page: studentSurfaces.muted,
  muted: studentSurfaces.textMuted,
};

const FONT = `'Tajawal','Amiri','Scheherazade New','Noto Sans Arabic','Cairo',sans-serif`;

const ExamMainData = ({
  exam,
  state,
  solveExamFun,
  checkExamAnswersFun,
  returnTo,
  allowStart = true,
  canContinue = false,
  showStatus = true,
  alwaysShowCheckAnswers = false,
  icon = "exam",
  onViewPdf,
}) => {
  const dispatch = useDispatch();
  const studentGroupId = useSelector((s) => s.user.userData?.group?.id);
  const [isLoading, setLoading] = useState(false);
  const [isExamPdfOpen, setExamPdfOpen] = useState(false);
  const isSolved = state === "Solved";
  const timed = exam?.timer == 1;
  const startsIn = useStartsInLabel(exam?.start_time, 1000);
  const deadline = useStudentExamDeadline(exam, studentGroupId, 1000);
  const deadlineLine = formatStudentExamDeadlineLine(deadline);
  const closed = deadline.closed;
  const localStart = formatApiDateTimeLocal(exam?.start_time);
  const startDisabled =
    Number(exam?.enable) === 1 || Boolean(startsIn) || closed;
  const examPdfUrl = useMemo(() => examPaperPdfUrl(exam?.pdf), [exam?.pdf]);
  const questionCount =
    exam?.total_count ?? exam?.question_number ?? exam?.questions_count;
  const examTags = useMemo(() => {
    const raw = exam?.tags || exam?.exam_tags || [];
    if (!Array.isArray(raw)) return [];
    return raw
      .map((tag) => (typeof tag === "string" ? tag : tag?.name))
      .filter(Boolean);
  }, [exam?.tags, exam?.exam_tags]);

  const persistReturnAndStart = () => {
    if (!canContinue && startDisabled) return;
    if (returnTo) {
      try {
        sessionStorage.setItem(POST_EXAM_REDIRECT_KEY, returnTo);
      } catch {
        /* ignore */
      }
    }
    solveExamFun(exam);
  };

  return (
    <VStack
      align="stretch"
      spacing={4}
      w="100%"
      h="100%"
      fontFamily={FONT}
      bg={studentSurfaces.raised}
      borderWidth="1px"
      borderColor={C.border}
      borderRadius="2xl"
      overflow="hidden"
      boxShadow="0 8px 28px rgba(91, 13, 22, 0.08)"
    >
      <Box
        position="relative"
        pl={4}
        py={5}
        pr={5}
        bg={studentSurfaces.muted}
        sx={{
          "html[data-theme='dark'] &": {
            background: studentSurfaces.muted,
          },
        }}
      >
        <Box position="absolute" left={0} top={0} bottom={0} w="4px" bg={C.primary} borderRadius="full" />
        <HStack align="flex-start" spacing={4}>
          {icon === "tag" ? (
            <Flex
              align="center"
              justify="center"
              w="48px"
              h="48px"
              flexShrink={0}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={C.border}
              bg={studentSurfaces.raised}
              color={C.primary}
              sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
            >
              <MdLocalOffer size={26} />
            </Flex>
          ) : (
            <Avatar name={exam?.name} src={bg} borderRadius="lg" size="md" bg="transparent" borderWidth="1px" borderColor={C.border} />
          )}
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontWeight="800" color={C.accent} fontSize="md" noOfLines={2} lineHeight="short">
              {exam?.name}
            </Text>
            <Text fontSize="xs" color={C.muted} fontWeight="600">
              {exam?.created_at ? dayjs(exam.created_at).format("D MMM YYYY") : ""}
            </Text>
          </VStack>
        </HStack>
      </Box>

      <VStack align="stretch" spacing={3} px={5} pb={2} flex={1}>
        {showStatus ? (
          <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
            <HStack color={C.primary} sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}>
              <MdVerified size={18} />
              <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.14em">
                Status
              </Text>
            </HStack>
            <Box
              px={3}
              py={1}
              borderRadius="full"
              bg={isSolved ? "rgba(91, 13, 22, 0.12)" : "rgba(192, 74, 88, 0.18)"}
              color={C.accent}
              fontSize="xs"
              fontWeight="800"
              textTransform="uppercase"
              letterSpacing="0.08em"
            >
              {state}
            </Box>
          </Flex>
        ) : null}

        {timed ? (
          <Flex align="center" justify="space-between" fontSize="sm" color={C.accent}>
            <HStack>
              <MdSchedule size={18} />
              <Text fontWeight="700">Time limit</Text>
            </HStack>
            <Text fontWeight="800">{exam?.time} min</Text>
          </Flex>
        ) : null}

        {questionCount != null ? (
          <Flex align="center" justify="space-between" fontSize="sm" color={C.muted}>
            <HStack>
              <MdAssignment size={18} />
              <Text fontWeight="700">Questions</Text>
            </HStack>
            <Text
              fontWeight="800"
              color={C.primary}
              sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
            >
              {questionCount}
            </Text>
          </Flex>
        ) : null}

        {examTags.length > 0 ? (
          <Wrap spacing={2}>
            {examTags.map((name) => (
              <WrapItem key={name}>
                <Tag
                  size="sm"
                  borderRadius="full"
                  bg="rgba(91, 13, 22, 0.12)"
                  color={C.accent}
                  fontWeight="700"
                >
                  {name}
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        ) : null}

        {isSolved && exam?.score != null ? (
          <>
            <Divider borderColor={C.border} />
            <Box borderRadius="xl" bg={C.page} px={4} py={3} borderWidth="1px" borderColor={C.border}>
              <Text fontSize="10px" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.12em" mb={1}>
                Your grade
              </Text>
              <Text
                fontSize="2xl"
                fontWeight="800"
                color={C.primary}
                lineHeight="1.2"
                sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
              >
                {exam?.right_answers == null ? "—" : exam.right_answers}
                <Text as="span" fontSize="md" fontWeight="700" color={C.muted}>
                  {" "}
                  / {exam.score}
                </Text>
              </Text>
            </Box>
          </>
        ) : null}
      </VStack>

      <VStack px={5} pb={5} pt={0} spacing={3} align="stretch" mt="auto">
        {examPdfUrl || onViewPdf ? (
          <Button
            w="100%"
            variant="outline"
            borderWidth="2px"
            borderColor={C.primary}
            color={C.primary}
            borderRadius="xl"
            fontWeight="800"
            leftIcon={<MdDescription size={18} />}
            sx={studentOutlineButtonDarkSx}
            onClick={() => (onViewPdf ? onViewPdf(exam) : setExamPdfOpen(true))}
          >
            View PDF
          </Button>
        ) : null}
        {isSolved || alwaysShowCheckAnswers ? (
          <VStack w="100%" spacing={3} align="stretch">
            <Button
              w="100%"
              bg={C.primary}
              color="white"
              borderRadius="xl"
              fontWeight="800"
              size="md"
              onClick={() => checkExamAnswersFun(exam)}
              isDisabled={alwaysShowCheckAnswers ? false : exam?.correction != 1}
              _hover={{ bg: C.accent }}
            >
              Check answers
            </Button>
            {isSolved ? (
              <Button
                w="100%"
                isLoading={isLoading}
                variant="outline"
                borderWidth="2px"
                borderColor={C.primary}
                color={C.primary}
                borderRadius="xl"
                fontWeight="800"
                sx={studentOutlineButtonDarkSx}
                onClick={() => {
                  setLoading(true);
                  const formData = new FormData();
                  formData.append("exam_id", exam?.id);
                  const action = () => setLoading(false);
                  dispatch(examRedoRequest(formData, action));
                }}
                isDisabled={exam?.redo_flag == 1 ? false : true}
              >
                Request redo
              </Button>
            ) : canContinue ? (
              <Button
                w="100%"
                variant="outline"
                borderWidth="2px"
                borderColor={C.primary}
                color={C.primary}
                borderRadius="xl"
                fontWeight="800"
                sx={studentOutlineButtonDarkSx}
                onClick={persistReturnAndStart}
              >
                Continue exam
              </Button>
            ) : null}
          </VStack>
        ) : allowStart || canContinue ? (
          <VStack align="stretch" spacing={2} w="100%">
            {!closed && startsIn && localStart ? (
              <Text
                fontSize="sm"
                fontWeight="800"
                color={C.primary}
                textAlign="center"
                sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
              >
                Opens {localStart}
              </Text>
            ) : null}
            {!closed && deadlineLine ? (
              <Text
                fontSize="sm"
                fontWeight="800"
                color={C.primary}
                textAlign="center"
                sx={{ "html[data-theme='dark'] &": { color: "var(--text-primary)" } }}
              >
                {deadlineLine}
              </Text>
            ) : null}
            <Button
              w="100%"
              bg={C.primary}
              color="white"
              borderRadius="xl"
              fontWeight="800"
              size="lg"
              py={7}
              isDisabled={!canContinue && startDisabled}
              onClick={persistReturnAndStart}
              _hover={{ bg: C.accent }}
              _disabled={{ opacity: 0.55, cursor: "not-allowed" }}
              boxShadow="0 10px 24px rgba(91, 13, 22, 0.2)"
            >
              {closed ? "Exam ended" : startsIn ? startsIn : canContinue ? "Continue exam" : "Start your exam"}
            </Button>
          </VStack>
        ) : (
          <Text fontSize="sm" color={C.muted} fontWeight="600" textAlign="center">
            Questions are only issued when you create an exam. Start a new one above.
          </Text>
        )}
      </VStack>

      <PdfPreviewModal
        isOpen={isExamPdfOpen}
        onClose={() => setExamPdfOpen(false)}
        pdfUrl={examPdfUrl}
        title={exam?.name || "Exam PDF"}
        backLabel="Back"
      />
    </VStack>
  );
};

export default ExamMainData;
